import styled, { keyframes } from 'styled-components'
import { formatUnits } from 'viem'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { fmtCompact } from 'views/TokenPages/format'
import type { ListItem } from 'views/TokenPages/types'
import { typeOf } from 'views/TradingCompetitionV3/config'
import { useTokenMeta } from 'views/TradingCompetitionV3/hooks'
import { COMPETITIONS, CREATE, competitionPath, legacyPath } from 'views/TradingCompetitionV3/routes'
import type { BoardCompetition } from './logic'
import { timeLeft } from './logic'
import { DISPLAY, Ellipsis, Fluent, HUES, HeadLink, ICE, MINT, MONO, MUTED, Panel, PanelHead, RowLink, SNOW, SNOW_2, Skel } from './ui'

/*
 * "Live now": the competitions running or about to start, on both contracts. Three row slots, always: a missing row becomes the "start one"
 * row, so the panel is the same height loading, empty and full (on wide screens it sits beside the headline and must not push the tiles).
 */
export const ROW_H = 64
const SLOTS = 3

const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(57, 217, 138, 0.6); } 70% { box-shadow: 0 0 0 8px rgba(57, 217, 138, 0); } 100% { box-shadow: 0 0 0 0 rgba(57, 217, 138, 0); }`
const Dot = styled.span`
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: 50%;
  background: ${MINT};
  animation: ${pulse} 2s ease-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const List = styled.div`
  display: grid;
  grid-template-rows: repeat(${SLOTS}, ${ROW_H}px);
  gap: 6px;
`
const Row = styled(RowLink)`
  grid-template-columns: 40px minmax(0, 1fr) auto;
  .name {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
  }
  .meta {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
  }
  .right {
    text-align: right;
    min-width: 0;
  }
  .prize {
    display: block;
    font-family: ${MONO};
    font-weight: 700;
    font-size: 13px;
    line-height: 18px;
    color: ${HUES.amber.text};
    white-space: nowrap;
  }
  .when {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: ${SNOW_2};
    white-space: nowrap;
  }
`
const StartRow = styled(RowLink)`
  grid-template-columns: 40px minmax(0, 1fr) auto;
  border: 1px dashed rgba(${HUES.amber.rgb}, 0.45);
  background: rgba(${HUES.amber.rgb}, 0.05);
  .t {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    color: ${SNOW};
  }
  .s {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
  }
  .go {
    font-weight: 800;
    font-size: 13px;
    color: ${HUES.amber.text};
    white-space: nowrap;
  }
`
const Ring = styled.span<{ $live: boolean }>`
  display: inline-flex;
  border-radius: 50%;
  box-shadow: ${({ $live }) => ($live ? `0 0 0 2px ${MINT}, 0 0 14px rgba(57, 217, 138, 0.45)` : `0 0 0 2px rgba(${HUES.ice.rgb}, 0.6)`)};
`
const SkelRow = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 72px;
  align-items: center;
  column-gap: 12px;
  height: ${ROW_H}px;
  padding: 0 10px;
`
const Note = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  height: ${ROW_H}px;
  padding: 0 10px;
  font-size: 14px;
  line-height: 20px;
  color: ${SNOW_2};
`

function CompetitionLine({ c, tokens, now }: { c: BoardCompetition; tokens: Record<string, ListItem>; now: number | null }) {
  const listed = tokens[c.projectToken.toLowerCase()]
  const project = useTokenMeta(listed ? undefined : c.projectToken, true)
  const reward = useTokenMeta(c.rewardToken, true)
  const symbol = listed?.symbol ?? project.symbol ?? '…'
  const prize = reward.decimals !== undefined ? fmtCompact(Number(formatUnits(BigInt(c.rewardAmount), reward.decimals))) : null
  const live = c.phase === 'live'
  const when = now === null ? '' : live ? `ends in ${timeLeft(c.endTime - now)}` : `starts in ${timeLeft(c.startTime - now)}`
  const kind = typeOf(c.type)?.short ?? 'Competition'
  return (
    <Row $h={ROW_H} href={c.legacy ? legacyPath(c.id) : competitionPath(c.id)} data-testid="home-live-row" data-phase={c.phase}>
      <Ring $live={live}>
        <TokenLogo srcs={[listed?.logoUrl]} address={c.projectToken} symbol={symbol} size={40} />
      </Ring>
      <span style={{ minWidth: 0 }}>
        <Ellipsis className="name">{symbol} competition</Ellipsis>
        <Ellipsis className="meta">
          {kind} · {c.numberOfWinners} winners
        </Ellipsis>
      </span>
      <span className="right">
        <span className="prize">{prize !== null ? `${prize} ${reward.symbol ?? ''}`.trim() : reward.loading ? <Skel $w={72} $h={12} /> : null}</span>
        <span className="when">{when}</span>
      </span>
    </Row>
  )
}

export function LivePanel({
  rows,
  live,
  upcoming,
  loading,
  failed,
  tokens,
  now,
}: {
  rows: BoardCompetition[]
  live: number
  upcoming: number
  loading: boolean
  failed: boolean
  tokens: Record<string, ListItem>
  now: number | null
}) {
  const facts = loading ? 'Reading the competitions' : failed ? 'Could not be read right now' : `${live} live · ${upcoming} upcoming`
  const slots: JSX.Element[] = []
  if (loading) {
    for (let i = 0; i < SLOTS; i++)
      slots.push(
        <SkelRow key={`s${i}`} aria-hidden>
          <Skel $w={40} $h={40} $r={999} />
          <span style={{ display: 'grid', gap: 6 }}>
            <Skel $w="70%" $h={14} />
            <Skel $w="45%" $h={10} />
          </span>
          <Skel $w={72} $h={14} />
        </SkelRow>,
      )
  } else {
    rows.slice(0, SLOTS).forEach((c) => slots.push(<CompetitionLine key={`${c.legacy ? 'v3' : 'v4'}-${c.id}`} c={c} tokens={tokens} now={now} />))
    if (failed) slots.push(<Note key="failed">The list did not load. Open the competitions page to see them.</Note>)
    else if (rows.length === 0) slots.push(<Note key="none">No competition is running right now.</Note>)
    if (slots.length < SLOTS) {
      const k = SLOTS - slots.length
      slots.push(
        <StartRow key="start" $h={k * ROW_H + (k - 1) * 6} style={{ gridRow: `span ${k}` }} href={CREATE} data-testid="home-live-start">
          <Fluent name="trophy" size={36} />
          <span style={{ minWidth: 0 }}>
            <Ellipsis className="t">Your token here</Ellipsis>
            <Ellipsis className="s">Start a competition, set the prize</Ellipsis>
          </span>
          <span className="go">Start →</span>
        </StartRow>,
      )
    }
  }
  return (
    <Panel $glow="mint" aria-labelledby="home-live-title" data-testid="home-live-panel" data-state={loading ? 'loading' : failed ? 'error' : rows.length ? 'ready' : 'empty'}>
      <PanelHead>
        <Dot aria-hidden />
        <span className="grow">
          <h2 id="home-live-title">Live now</h2>
          <span className="facts">{facts}</span>
        </span>
        <HeadLink href={COMPETITIONS} style={{ color: ICE }}>
          All competitions
        </HeadLink>
      </PanelHead>
      <List>{slots}</List>
    </Panel>
  )
}

